import { ContentParser } from '@common/components/ContentParser';
import useRoomCapacityText from '@common/hooks/useRoomCapacityText';
import { useTranslations } from '@lang';
import { RoomUpgrade } from 'shared-types/Rate';
import type { BasketTemplateProps, RoomUpgradeOffer } from 'voyager';

type RoomUpgradeDialogLabels = NonNullable<
  NonNullable<BasketTemplateProps['upgradeDialog']>['labels']
>;

type RoomUpgradeOfferSource = Pick<
  RoomUpgrade,
  | 'name'
  | 'imageUrl'
  | 'description'
  | 'amenities'
  | 'beds'
  | 'priceDifference'
  | 'pricePerStay'
  | 'pricePerNight'
  | 'quantity'
  | 'displayUrgencyMessaging'
  | 'maxOccupancy'
  | 'maxAdults'
  | 'maxChildren'
>;

export const useRoomUpgradeOffer = (
  upgrade: RoomUpgradeOfferSource | undefined
): RoomUpgradeOffer | undefined => {
  const { t } = useTranslations();
  const capacityText = useRoomCapacityText({
    maxAdults: upgrade?.maxAdults,
    maxChildren: upgrade?.maxChildren,
    maxOccupancy: upgrade?.maxOccupancy,
  });

  if (!upgrade) return undefined;

  const { quantity, displayUrgencyMessaging, description } = upgrade;

  return {
    name: upgrade.name,
    imageUrl: upgrade.imageUrl,
    capacityText,
    bedsText: upgrade.beds?.types?.map((bed) => `${bed.quantity} ${bed.name}`).join(' • '),
    urgencyText:
      displayUrgencyMessaging && quantity ? t('room.hurryMessage', { quantity }) : undefined,
    description: description ? <ContentParser text={description} escapeHtml={false} /> : undefined,
    features: upgrade.amenities ?? undefined,
    priceDifference: upgrade.priceDifference,
    pricePerStay: upgrade.pricePerStay,
    pricePerNight: upgrade.pricePerNight,
  };
};

export const useRoomUpgradeDialogLabels = (): Required<RoomUpgradeDialogLabels> => {
  const { t } = useTranslations();
  return {
    perNight: t('room.night'),
    totalDue: t('room.upgrade.totalDue'),
    skip: t('room.upgrade.noThanks'),
    upgrade: t('room.upgrade'),
    close: t('room.upgrade.close'),
    previousImage: t('product.previousImage'),
    nextImage: t('product.nextImage'),
    goToImage: (position, total) => t('product.viewImage', { position, total }),
  };
};
